/**
 * /my/profile — Employee self-service profile (Section 2C).
 *
 * Single page with four tabs: Personal, Address, Emergency Contacts,
 * Preferences. A completion card at the top surfaces whatever's missing.
 *
 * The server enforces the editable whitelist so it's safe to surface
 * read-only fields next to editable ones in the same UI — anything we
 * don't want changed simply isn't in ``MyProfileUpdateInput`` on the wire.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Camera,
  CheckCircle2,
  Circle,
  Lock,
  User,
  MapPin,
  Phone,
  Bell,
} from "lucide-react";
import { fullName, pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Tabs, TabPanel } from "@/components/ui/Tabs";
import { useToast } from "@/hooks/useToast";
import {
  getMyProfile,
  getProfileCompleteness,
  getNotificationPreferences,
  markProfileReviewed,
  setNotificationPreferences,
  updateMyProfile,
  uploadMyAvatar,
  type MyEmergencyContact,
  type MyProfile,
  type MyProfileAddress,
  type MyProfileUpdateInput,
  type NotificationPreferences,
  type ProfileCompleteness,
} from "@/api/myProfile";

const TIMEZONE_OPTIONS = [
  { value: "", label: "Use org default" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Europe/Madrid", label: "Madrid" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Australia/Sydney", label: "Sydney" },
];
const LOCALE_OPTIONS = [
  { value: "", label: "Use org default" },
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "es-ES", label: "Spanish" },
  { value: "fr-FR", label: "French" },
  { value: "de-DE", label: "German" },
  { value: "pt-BR", label: "Portuguese (Brazil)" },
];

function CompletenessCard({
  data,
  onReviewed,
}: {
  data: ProfileCompleteness | null;
  onReviewed: () => void;
}) {
  if (!data) return null;
  const labels = Object.values(data.detail);
  const missing = labels.filter((d) => !d.has);
  return (
    <Card className="mb-6">
      <div className="flex items-start gap-4">
        <div className="relative h-20 w-20 shrink-0">
          <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
            <circle
              cx="18"
              cy="18"
              r="15.915"
              fill="none"
              stroke="currentColor"
              className="text-border"
              strokeWidth="3"
            />
            <circle
              cx="18"
              cy="18"
              r="15.915"
              fill="none"
              stroke="currentColor"
              className="text-accent"
              strokeWidth="3"
              strokeDasharray={`${data.percentage}, 100`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-text-primary">
            {data.percentage}%
          </div>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold font-heading text-text-primary">
            Your profile is {data.percentage}% complete
          </p>
          <p className="text-xs text-text-muted mb-3">
            {missing.length === 0
              ? "Nice — everything is filled in."
              : `Add ${missing.length} more ${missing.length === 1 ? "item" : "items"} to reach 100%.`}
          </p>
          {missing.length > 0 && (
            <ul className="space-y-1 text-xs">
              {labels.map((d) => (
                <li key={d.label} className="flex items-center gap-2">
                  {d.has ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <Circle className="h-3.5 w-3.5 text-text-muted" />
                  )}
                  <span
                    className={
                      d.has ? "text-text-muted line-through" : "text-text-primary"
                    }
                  >
                    {d.label}
                  </span>
                  <span className="text-text-muted">(+{d.weight}%)</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onReviewed}>
          Mark reviewed
        </Button>
      </div>
    </Card>
  );
}

function AvatarUploader({
  profile,
  onUploaded,
}: {
  profile: MyProfile;
  onUploaded: (url: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const { addToast } = useToast();
  const [uploading, setUploading] = useState(false);

  const onSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      addToast("error", "Avatar must be a JPEG or PNG");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      addToast("error", "Avatar must be 5 MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadMyAvatar(file);
      onUploaded(res.avatar_url);
      addToast("success", "Photo updated");
    } catch {
      addToast("error", "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        aria-label="Change profile photo"
        className="relative group"
        disabled={uploading}
      >
        <Avatar
          firstName={profile.first_name}
          lastName={profile.last_name}
          src={profile.avatar_url}
          size="xl"
        />
        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera className="h-5 w-5 text-white" />
        </span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={onSelect}
      />
      <div>
        <p className="text-sm font-semibold font-heading text-text-primary">
          {fullName(profile.first_name, profile.last_name, profile.preferred_name)}
        </p>
        <p className="text-xs text-text-muted">{profile.title}</p>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="text-xs font-medium text-accent hover:underline mt-1 disabled:opacity-50"
        >
          {uploading ? "Uploading…" : "Change photo"}
        </button>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <label className="block text-xs font-medium text-text-muted mb-0.5">
        {label}
      </label>
      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-text-primary">
        <span className="flex-1">{value || "—"}</span>
        <Lock className="h-3.5 w-3.5 text-text-muted" aria-label="Read-only" />
      </div>
    </div>
  );
}

export function MyProfilePage() {
  const { addToast } = useToast();
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [completeness, setCompleteness] = useState<ProfileCompleteness | null>(null);
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [tab, setTab] = useState("personal");
  const [saving, setSaving] = useState(false);

  // Editable draft state — initialized from the loaded profile.
  const [preferredName, setPreferredName] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [phone, setPhone] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");
  const [timezone, setTimezone] = useState("");
  const [locale, setLocale] = useState("");
  const [address, setAddress] = useState<MyProfileAddress>({});
  const [contacts, setContacts] = useState<MyEmergencyContact[]>([]);

  const refreshCompleteness = useCallback(() => {
    getProfileCompleteness()
      .then(setCompleteness)
      .catch(() => {});
  }, []);

  const hydrate = useCallback((p: MyProfile) => {
    setProfile(p);
    setPreferredName(p.preferred_name ?? "");
    setPronouns(p.pronouns ?? "");
    setPhone(p.phone ?? "");
    setPersonalEmail(p.personal_email ?? "");
    setTimezone(p.timezone ?? "");
    setLocale(p.locale ?? "");
    setAddress(p.address_json ?? {});
    setContacts(p.emergency_contacts_json ?? []);
  }, []);

  useEffect(() => {
    getMyProfile()
      .then(hydrate)
      .catch(() => addToast("error", "Failed to load your profile"));
    refreshCompleteness();
    getNotificationPreferences()
      .then(setPrefs)
      .catch(() => {});
  }, [addToast, hydrate, refreshCompleteness]);

  const saveSection = async (payload: MyProfileUpdateInput, label: string) => {
    setSaving(true);
    try {
      const updated = await updateMyProfile(payload);
      hydrate(updated);
      refreshCompleteness();
      addToast("success", `${label} saved`);
    } catch {
      addToast("error", `Failed to save ${label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const savePersonal = () =>
    saveSection(
      {
        preferred_name: preferredName || null,
        pronouns: pronouns || null,
        phone: phone || null,
        personal_email: personalEmail || null,
      },
      "Personal info"
    );

  const savePreferences = () =>
    saveSection(
      {
        timezone: timezone || null,
        locale: locale || null,
      },
      "Preferences"
    );

  const saveAddress = () =>
    saveSection({ address_json: address }, "Address");

  const saveContacts = () => {
    const clean = contacts
      .map((c) => ({
        ...c,
        relationship: c.relationship || null,
        phone: c.phone || null,
        email: c.email || null,
      }))
      .filter((c) => c.name.trim());
    return saveSection(
      { emergency_contacts_json: clean },
      "Emergency contacts"
    );
  };

  const addContact = () =>
    setContacts((prev) =>
      prev.length >= 3
        ? prev
        : [...prev, { name: "", relationship: "", phone: "", email: "" }]
    );

  const updateContact = (idx: number, patch: Partial<MyEmergencyContact>) =>
    setContacts((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  const removeContact = (idx: number) =>
    setContacts((prev) => prev.filter((_, i) => i !== idx));

  const markReviewed = async () => {
    try {
      await markProfileReviewed();
      refreshCompleteness();
      addToast("success", "Thanks — profile marked as reviewed");
    } catch {
      addToast("error", "Couldn't mark as reviewed");
    }
  };

  const updatePref = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      await setNotificationPreferences(next);
    } catch {
      addToast("error", "Failed to save preference");
      setPrefs(prefs);
    }
  };

  const tabs = useMemo(
    () => [
      { id: "personal", label: "Personal" },
      { id: "address", label: "Address" },
      { id: "contacts", label: "Emergency Contacts" },
      { id: "preferences", label: "Preferences" },
    ],
    []
  );

  if (!profile) {
    return (
      <motion.div {...pageTransition}>
        <PageHeader title="My Profile" subtitle="Loading…" />
      </motion.div>
    );
  }

  return (
    <motion.div {...pageTransition}>
      <PageHeader title="My Profile" subtitle="Keep your info up to date." />

      <CompletenessCard data={completeness} onReviewed={markReviewed} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-4">
          <Tabs tabs={tabs} activeTab={tab} onChange={setTab} aria-label="Profile sections" />

          <TabPanel id="personal" activeTab={tab}>
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <User className="h-4 w-4 text-accent" />
                <h3 className="text-sm font-semibold text-text-primary">
                  Personal information
                </h3>
              </div>
              <AvatarUploader
                profile={profile}
                onUploaded={(url) => setProfile({ ...profile, avatar_url: url })}
              />
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Preferred name"
                  value={preferredName}
                  onChange={(e) => setPreferredName(e.target.value)}
                />
                <Input
                  label="Pronouns"
                  value={pronouns}
                  onChange={(e) => setPronouns(e.target.value)}
                  placeholder="they/them, she/her, he/him"
                />
                <Input
                  label="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  icon={<Phone className="h-4 w-4" />}
                />
                <Input
                  label="Personal email"
                  value={personalEmail}
                  onChange={(e) => setPersonalEmail(e.target.value)}
                  type="email"
                  placeholder="you@personal.com"
                />
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={savePersonal} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </Card>
          </TabPanel>

          <TabPanel id="address" activeTab={tab}>
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-4 w-4 text-accent" />
                <h3 className="text-sm font-semibold text-text-primary">
                  Home address
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Input
                    label="Street"
                    value={address.street ?? ""}
                    onChange={(e) => setAddress({ ...address, street: e.target.value })}
                  />
                </div>
                <Input
                  label="City"
                  value={address.city ?? ""}
                  onChange={(e) => setAddress({ ...address, city: e.target.value })}
                />
                <Input
                  label="State / Region"
                  value={address.state ?? ""}
                  onChange={(e) => setAddress({ ...address, state: e.target.value })}
                />
                <Input
                  label="ZIP / Postal"
                  value={address.zip ?? ""}
                  onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                />
                <Input
                  label="Country"
                  value={address.country ?? ""}
                  onChange={(e) => setAddress({ ...address, country: e.target.value })}
                />
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={saveAddress} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </Card>
          </TabPanel>

          <TabPanel id="contacts" activeTab={tab}>
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-text-primary">
                  Emergency contacts
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addContact}
                  disabled={contacts.length >= 3}
                >
                  + Add contact
                </Button>
              </div>
              {contacts.length === 0 ? (
                <p className="text-sm text-text-muted py-6 text-center">
                  No emergency contacts yet. Add up to 3 people we can reach in an
                  emergency.
                </p>
              ) : (
                <div className="space-y-4">
                  {contacts.map((c, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-border p-3 space-y-3"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input
                          label="Name"
                          value={c.name}
                          onChange={(e) => updateContact(idx, { name: e.target.value })}
                        />
                        <Input
                          label="Relationship"
                          value={c.relationship ?? ""}
                          onChange={(e) => updateContact(idx, { relationship: e.target.value })}
                          placeholder="Spouse, parent, friend…"
                        />
                        <Input
                          label="Phone"
                          value={c.phone ?? ""}
                          onChange={(e) => updateContact(idx, { phone: e.target.value })}
                        />
                        <Input
                          label="Email"
                          type="email"
                          value={c.email ?? ""}
                          onChange={(e) => updateContact(idx, { email: e.target.value })}
                        />
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeContact(idx)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end mt-4">
                <Button onClick={saveContacts} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </Card>
          </TabPanel>

          <TabPanel id="preferences" activeTab={tab}>
            <Card>
              <h3 className="text-sm font-semibold text-text-primary mb-4">
                Language &amp; timezone
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  label="Timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  options={TIMEZONE_OPTIONS}
                />
                <Select
                  label="Language"
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                  options={LOCALE_OPTIONS}
                />
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={savePreferences} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </Card>

            {prefs && (
              <Card className="mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Bell className="h-4 w-4 text-accent" />
                  <h3 className="text-sm font-semibold text-text-primary">
                    Notification preferences
                  </h3>
                </div>
                <ul className="divide-y divide-border">
                  {([
                    { key: "leave_updates", label: "Leave request updates" },
                    { key: "document_assignments", label: "Document assignments" },
                    { key: "announcements", label: "Company announcements" },
                    { key: "agent_notifications", label: "AI agent notifications" },
                    { key: "push_notifications", label: "Push notifications" },
                  ] as { key: keyof NotificationPreferences; label: string }[]).map(
                    ({ key, label }) => (
                      <li
                        key={key}
                        className="flex items-center justify-between py-2.5"
                      >
                        <span className="text-sm text-text-primary">{label}</span>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={prefs[key]}
                            onChange={(e) => updatePref(key, e.target.checked)}
                          />
                          <span className="w-10 h-5 rounded-full bg-border peer-checked:bg-accent transition-colors relative after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
                        </label>
                      </li>
                    ),
                  )}
                </ul>
              </Card>
            )}
          </TabPanel>
        </div>

        {/* Right column — work info (read-only) */}
        <div>
          <Card>
            <h3 className="text-sm font-semibold font-heading text-text-primary mb-1">
              Work info
            </h3>
            <p className="text-xs text-text-muted mb-4">
              Managed by HR. Need a change? Contact your HR team.
            </p>
            <div className="space-y-3">
              <ReadOnlyField label="Legal name" value={`${profile.first_name} ${profile.last_name}`} />
              <ReadOnlyField label="Work email" value={profile.work_email} />
              <ReadOnlyField label="Title" value={profile.title} />
              <ReadOnlyField label="Department" value={profile.department_name} />
              <ReadOnlyField label="Manager" value={profile.manager_name} />
              <ReadOnlyField
                label="Hire date"
                value={new Date(profile.hire_date).toLocaleDateString()}
              />
              <div>
                <label className="block text-xs font-medium text-text-muted mb-0.5">
                  Employment type
                </label>
                <Badge variant="info">{profile.employment_type}</Badge>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </motion.div>
  );
}
